import { useSelect } from '@wordpress/data';
import {
    hideField,
    checkUserRoleConditions,
    checkProductConditionsForField,
    updateFieldValuesForHiddenFields,
    formatOptionPriceDisplay,
    formatFieldPriceDisplay,
    getFieldPriceType,
} from "./input-validations";

export { formatOptionPriceDisplay, formatFieldPriceDisplay, getFieldPriceType };

let recalcTimeout = null; // keep this outside the function, module-level

const CART_CONDITION_FIELDS = ['fme_ccfw_cart_total', 'fme_ccfw_cart_subtotal'];

const normalizeCartConditionValue = (fieldName, value) => {
    if (!CART_CONDITION_FIELDS.includes(fieldName) || value === '' || value === null || value === undefined) {
        return value;
    }
    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) {
        return value;
    }
    // WooCommerce Blocks exposes cart totals in minor units (e.g. cents).
    if (numericValue >= 100 && Number.isInteger(numericValue)) {
        return numericValue / 100;
    }
    return numericValue;
};

// export const fieldIds = []; // Use Set to avoid duplicates

/**
 * Determine if a field should be hidden based on its conditions
 *
 * @param {Object} field - The field object with `cfield` and `value`
 * @param {Object} cart - Cart data
 * @returns {boolean} - Whether the field should be hidden
*/

export const shouldHideField = (field, cart = {}, currentFieldValue) => {

    const fieldName = field.field_name;
    const conditions = field.cfield[0];

    if (!Array.isArray(conditions) || conditions.length === 0) return false;

    const allFieldValues = fme_all_fields_data.reduce((acc, f) => {
        acc[f.field_name] = f.value ?? '0'; // default to empty string
        return acc;
    }, {});

    const resolveValue = (target) => {

        // 1. If the target is the current field itself
        if (target === fieldName) {
            return field.value ?? jQuery('#' + target).val();  // prefer passed field.value
        }

        // 2. Billing fields
        if (target.startsWith('billing_')) {
            const key = target.replace('billing_', '');
            const billing = cart?.billingAddress || {};
            if (key in billing) return billing[key];
        }

        // 3. Shipping fields
        if (target.startsWith('shipping_')) {
            const key = target.replace('shipping_', '');
            const shipping = cart?.shippingAddress || {};
            if (key in shipping) return shipping[key];
        }

        // 4. Other form fields – get from DOM
        const domVal = jQuery('#' + target).val();
        if (typeof domVal !== 'undefined') return domVal;

        // 5. Fallback to preloaded values
        if (target in allFieldValues) return allFieldValues[target];


        const fallback = fme_all_fields_data.find(f => f.field_name === target);
        return fallback?.value ?? undefined;

    };

    const isEmpty = val => val === undefined || val === null || val === '';
    const isTruthy = val => val === true || val === 'yes' || val === 'on' || val === '1' || val === 1;

    for (const [action, target, operator, value] of conditions) {

        const targetValue = resolveValue(target);

        const numericTarget = parseFloat(targetValue);
        const numericValue = parseFloat(value);

        let conditionMet = false;

        switch (operator) {
            case 'is_empty':
                conditionMet = isEmpty(targetValue);
                break;
            case 'is_not_empty':
                conditionMet = !isEmpty(targetValue);
                break;
            case 'is_equal_to':
                conditionMet = String(targetValue) === String(value);
                break;
            case 'is_not_equal_to':
                conditionMet = String(targetValue) !== String(value);
                break;
            case 'is_greater_than':
                conditionMet = numericTarget > numericValue;
                break;
            case 'is_less_than':
                conditionMet = numericTarget < numericValue;
                break;
            case 'is_checked':
                conditionMet = isTruthy(targetValue);
                break;
            default:
                conditionMet = false;
        }

        if (action === 'Hide' && conditionMet) return true;     // Hide if any Hide condition is met
        if (action === 'Show' && !conditionMet) return true;    // Hide if Show condition not met

    }

    return false; // Default: don't hide
};

export const isDefaultField = (fieldName) => {
    const defaultFieldNameMap = getDefaultFieldsName();
    return defaultFieldNameMap.hasOwnProperty(fieldName);
}


// This function to get conditional Fields data of fields - wn basis of that fields will hide and show
export const getTargetConditionsToHideShowFields = (fme_all_fields_data, fieldValues) => {

    // map: targetFieldName (the field that owns cfield) => [ group1Array, group2Array, ... ]
    // where each group is an array of condition objects
    const targetConditionsMap = {};
    const shippingAsBillingSession = document.getElementById('billing-fields');

    fme_all_fields_data.forEach(sourceField => {

        // prepare field current value for the sourceField (the target of conditions)
        let fieldName = sourceField.field_name;
        fieldName = getFieldName(sourceField, fieldName);
        setFieldCurrentValue(sourceField, fieldName);

        const cfields = Array.isArray(sourceField.cfield) ? sourceField.cfield : [];

        // Each cfields item is a group (array of raw condition arrays).
        cfields.forEach(conditionGroup => {
            if (!Array.isArray(conditionGroup) || conditionGroup.length === 0) return;

            // resolved target (the field that owns this cfield group)
            const targetKey = sourceField.field_name;
            if (!targetConditionsMap[targetKey]) targetConditionsMap[targetKey] = [];

            // build group of condition objects (do not mutate original arrays)
            const groupObjects = conditionGroup.map(condition => {
                if (!Array.isArray(condition)) return null;

                const action = condition[0] ?? null;
                const controllerName = condition[1] ?? null; // the field which controls this target
                const operator = condition[2] ?? null;
                const expectedValue = condition[3] ?? null;
                const extra = condition.slice(4);



                let controllerNameIfShippingAsBilling = controllerName;
                if (null == shippingAsBillingSession) {
                    controllerNameIfShippingAsBilling = controllerNameIfShippingAsBilling.replace('billing_', 'shipping_');
                }

                let targetField = fme_all_fields_data.find(f => f.field_name === controllerNameIfShippingAsBilling);
                let targetFieldType = targetField?.field_type;

                // resolve controller (source) current value
                const isDefault = isDefaultField(controllerNameIfShippingAsBilling);
                const resolvedControllerName = getFieldName(sourceField, controllerNameIfShippingAsBilling);
                let sourceFieldValue = !isDefault ? fieldValues[controllerNameIfShippingAsBilling] : fieldValues[resolvedControllerName] || getFieldValue(resolvedControllerName);
                sourceFieldValue = normalizeCartConditionValue(controllerNameIfShippingAsBilling, sourceFieldValue);

                // Normalize multiselect - keep as array for proper evaluation
                // The evaluation function will handle the normalization
                if (targetFieldType === 'multiselect' || targetFieldType === 'checkbox_group' && !Array.isArray(sourceFieldValue)) {
                    // If it's a string, try to parse it
                    try {
                        sourceFieldValue = JSON.parse(sourceFieldValue);
                    } catch (e) {
                        // Keep as is if not a valid JSON
                    }
                }

                // resolved target value (the field that owns this cfield)
                const resolvedTargetFieldName = getFieldName(sourceField, sourceField.field_name);
                const targetFieldValue = fieldValues[resolvedTargetFieldName] ?? getFieldValue(resolvedTargetFieldName);

                return {
                    action,
                    operator,
                    expectedValue,
                    sourceFieldValue,
                    // set targetFieldName to the controller referenced in condition (as requested)
                    targetFieldName: controllerNameIfShippingAsBilling,
                    targetFieldValue,
                    targetFieldType: sourceField.field_type,
                    targetSectionType: sourceField.type,
                    rawCondition: condition.slice(), // clone
                    extra
                };
            }).filter(Boolean);

            // append group of objects to map under the owning target key
            targetConditionsMap[targetKey].push(groupObjects);
        });
    });

    return targetConditionsMap;
}


// Main function to get updated fields with hide/show attribute ( add isHide attribute to fields  true/false)
export const getUpdatedFieldsWithHideShowAttribute = (targetConditionsMap, fields, allFields, fieldValues, cartDispatch) => {

    /**
     * Evaluate if a single condition matches
     * @param {Object} cond - Condition object with operator, values, etc
     * @returns {boolean} - true if condition matches, false otherwise
     */
    const isConditionMet = (cond) => {
        const {
            operator,
            expectedValue,
            sourceFieldValue,
            targetFieldName
        } = cond;

        // Normalize the source value
        let normalizedSourceValue = normalizeCartConditionValue(targetFieldName, sourceFieldValue);
        const sourceFieldType = allFields.find(f => f.field_name === targetFieldName)?.field_type;

        // Handle multiselect fields - extract keys from array of objects
        if (sourceFieldType === 'multiselect' || sourceFieldType === 'checkbox_group' && Array.isArray(sourceFieldValue)) {
            const selectedKeys = sourceFieldValue.map(item => item.key || item);
            
            // For is_equal_to/is_not_equal_to with multiselect, check if expected value is in selected keys
            if (operator === 'is_equal_to' || operator === 'is_not_equal_to') {
                normalizedSourceValue = selectedKeys;
            } else {
                // For other operators, join as comma-separated string
                normalizedSourceValue = selectedKeys.join(',');
            }
            
        }

        // Evaluate the operator
        let match = false;
        switch (operator) {
            case 'is_equal_to':
                // For multiselect (array), check if expected value is in the array
                if (Array.isArray(normalizedSourceValue)) {
                    match = normalizedSourceValue.includes(String(expectedValue));
                } else {
                    match = String(normalizedSourceValue) === String(expectedValue);
                }
                break;

            case 'is_not_equal_to':
                // For multiselect (array), check if expected value is NOT in the array
                if (Array.isArray(normalizedSourceValue)) {
                    match = !normalizedSourceValue.includes(String(expectedValue));
                } else {
                    match = String(normalizedSourceValue) !== String(expectedValue);
                }
                break;

            case 'is_empty':
                // For arrays, check if empty array; for strings, check if empty string
                if (Array.isArray(normalizedSourceValue)) {
                    match = normalizedSourceValue.length === 0;
                } else {
                    match = normalizedSourceValue === '' || normalizedSourceValue == null || normalizedSourceValue === undefined;
                }
                break;

            case 'is_not_empty':
                // For arrays, check if has items; for strings, check if not empty
                if (Array.isArray(normalizedSourceValue)) {
                    match = normalizedSourceValue.length > 0;
                } else {
                    match = normalizedSourceValue !== '' && normalizedSourceValue != null && normalizedSourceValue !== undefined;
                }
                break;

            case 'is_greater_than':
                match = !isNaN(normalizedSourceValue) && !isNaN(expectedValue) && 
                        parseFloat(normalizedSourceValue) > parseFloat(expectedValue);
                break;

            case 'is_less_than':
                match = !isNaN(normalizedSourceValue) && !isNaN(expectedValue) && 
                        parseFloat(normalizedSourceValue) < parseFloat(expectedValue);
                break;

            case 'is_checked':
                // For checkboxes, consider them checked if they have any non-empty value
                match = normalizedSourceValue !== '' && normalizedSourceValue != null && normalizedSourceValue !== undefined;
                break;

            default:
                console.warn('Unknown operator:', operator);
                match = false;
        }

        return match;
    };

    // ========================================
    // MAIN LOOP - Process each field's conditions
    // ========================================
    Object.entries(targetConditionsMap).forEach(([targetFieldName, conditionGroups]) => {

        // Default: show the field (don't hide)
        let shouldHide = false;

        // Early exit if no condition groups
        if (!conditionGroups || conditionGroups.length === 0) {
            return;
        }

        // Get the action type from first condition (assume all conditions have same action)
        const actionType = conditionGroups[0]?.[0]?.action;
        
        if (!actionType) {
            console.warn('No action type found for field:', targetFieldName);
            return;
        }

        // ----------------------------------------------
        // Evaluate all condition groups with OR logic
        // ----------------------------------------------
        const groupResults = conditionGroups.map((andGroup, groupIndex) => {
            
            // Within each group, ALL conditions must be met (AND logic)
            const allConditionsMet = andGroup.every((cond, condIndex) => {
                const met = isConditionMet(cond);
                return met;
            });

            return allConditionsMet;
        });

        // ----------------------------------------------
        // Apply OR logic between groups based on action
        // ----------------------------------------------
        const anyGroupPassed = groupResults.some(result => result);
        
        if (actionType === 'Show') {
            // SHOW logic: Hide field if NO group passes (show if any group passes)
            shouldHide = !anyGroupPassed;
        } else if (actionType === 'Hide') {
            // HIDE logic: Hide field if ANY group passes
            shouldHide = anyGroupPassed;
        }

        // ----------------------------------------------
        // Apply the hide/show decision to fields
        // ----------------------------------------------
        
        // Update in fields array
        fields = fields.map(field =>
            field.field_name === targetFieldName
                ? { ...field, isHide: shouldHide }
                : field
        );

        // Update in allFields array
        allFields = allFields.map(field =>
            field.field_name === targetFieldName
                ? { ...field, isHide: shouldHide }
                : field
        );

        // Clear field value marker if field is being shown and was previously hidden
        if (!shouldHide && fieldValues && Object.prototype.hasOwnProperty.call(fieldValues, targetFieldName) && '---' === fieldValues[targetFieldName]) {
            // Field value will be cleared by the hidden fields handler
        }

        // Handle default fields (billing/shipping address fields)
        const defaultFieldName = checkAndGetDefaultFieldName(targetFieldName);
        if ('not_default' !== defaultFieldName) {
            // Additional handling for default WooCommerce fields can go here
        }

    });

    // ========================================
    // Additional Checks: User Roles & Product Categories
    // ========================================
    
    fields = fields.map(field => {
        const fieldName = field.field_name;

        // Check 1: Field must be enabled
        if (field.is_enable == 0) {
            return { ...field, isHide: true };
        }

        // Check 2: User role conditions
        if (!checkUserRoleConditions(field)) {
            return { ...field, isHide: true };
        }

        // Check 3: Product/category conditions
        if (!checkProductConditionsForField(field)) {
            return { ...field, isHide: true };
        }

        return field;
    });

    // ========================================
    // Update fieldValues for hidden fields
    // ========================================
    // Mark hidden fields with '---' to prevent their values from being submitted
    fieldValues = updateFieldValuesForHiddenFields(fieldValues, fields);
    return { fields, allFields, fieldValues };
};

export const showHideField = (field, targetConditionsMap, baseFieldName, setHiddenFields, hiddenFieldsRef, setFieldValues, lastSentValues, cartDispatch) => {

    // 2. Apply visibility logic per target field
    Object.entries(targetConditionsMap).forEach(([sourceFieldName, conditions]) => {

        if (baseFieldName != sourceFieldName) return;


        let shouldHide = false;
        for (const cond of conditions) {

            const { action, operator, expectedValue, sourceFieldValue, targetFieldName, targetFieldType, targetFieldValue } = cond;
            let match = false;

            // Normalize multiselect source values into an array of strings
            let normalizedSourceValue = sourceFieldValue;


            if (targetFieldType === 'multiselect' || targetFieldType === 'checkbox_group' && Array.isArray(sourceFieldValue)) {
                // Extract either `key` or `value` from each item (depending on what you're comparing against)
                // You can choose 'key' or 'value' below based on your needs
                normalizedSourceValue = sourceFieldValue.map(item => item.key); // or item.value
            }


            switch (operator) {
                case 'is_equal_to':
                    match = String(normalizedSourceValue) === String(expectedValue);
                    break;
                case 'is_not_equal_to':
                    match = String(normalizedSourceValue) !== String(expectedValue);
                    break;
                case 'is_empty':
                    match = normalizedSourceValue === '' || normalizedSourceValue == null;
                    break;
                case 'is_not_empty':
                    match = normalizedSourceValue !== '' && normalizedSourceValue != null;
                    break;
                case 'is_greater_than':
                    match = parseFloat(normalizedSourceValue) > parseFloat(expectedValue);
                    break;
                case 'is_less_than':
                    match = parseFloat(normalizedSourceValue) < parseFloat(expectedValue);
                    break;
                case 'is_checked':
                    // For checkboxes, consider them checked if they have any non-empty value
                    match = normalizedSourceValue !== '' && normalizedSourceValue != null && normalizedSourceValue !== undefined;
                    break;
                default:
                    match = false;
            }

            if (action === 'Hide' && match) {
                shouldHide = true;
                break; // hide wins
            }

            if (action === 'Show' && !match) {
                shouldHide = true;
                break; // show fails => hide
            }

        }

        let targetElement = jQuery('#' + targetFieldName);
        targetFieldName = targetFieldName.trim();
        if (targetFieldName.includes('billing_')) {
            targetFieldName = targetFieldName.replace('billing_', 'billing-');
            targetElement = jQuery('#' + targetFieldName);

            if (!targetElement.length) {
                targetFieldName = targetFieldName.replace('billing-', 'shipping-');
                targetElement = jQuery('#' + targetFieldName);
            }

        }

        if (targetElement.length) {
            const container = targetElement.closest('.form-field');
            const containerFieldName = baseFieldName.replace('billing-', '').replace('shipping-', '').replace('additional-', '');
            const targetContainer = jQuery('.' + containerFieldName + '_field_container');

            if (targetContainer.length) {
                shouldHide ? targetContainer.hide() : targetContainer.show();
            }
        }

        // Update hidden field state
        setHiddenFields(prev => ({
            ...prev,
            [targetFieldName]: shouldHide,
        }));
        hiddenFieldsRef.current[targetFieldName] = shouldHide;

        if (shouldHide) {

            // Clear field value in session
            setFieldValues(prev => ({
                ...prev,
                [targetFieldName]: '',
                [targetFieldName + '_data']: null,
            }));

            lastSentValues.current[targetFieldName] = '';

            jQuery.ajax({
                url: fme_upload_data.ajax_url,
                type: 'POST',
                data: {
                    action: 'fme_store_fields_value_in_session',
                    fieldValue: {
                        fieldName: field.field_label,
                        fieldValue: '',
                        fieldFees: 0,
                        fieldType: field.field_type,
                    },
                    fieldId: targetFieldName,
                    security: fme_upload_data.upload_nonce
                },
                success: () => {
                },
            });

        }

    });


}

export const getFieldValue = (fieldName) => {
    let sourceFieldValue = '';

    if ('order_comments' == fieldName) {
        fieldName = '.wc-block-checkout__add-note .wc-block-components-textarea';
        sourceFieldValue = jQuery(fieldName).val() ?? '';
    } else {
        sourceFieldValue = jQuery('#' + fieldName).val() ?? '';
        if (Array.isArray(sourceFieldValue)) {
            sourceFieldValue = sourceFieldValue.map(item => item.key)[0];
        }
    }
    return sourceFieldValue;
}

export const setFieldCurrentValue = (sourceField, fieldName) => {

    if (undefined == sourceField.value) {
        sourceField.value = jQuery('#' + fieldName).val() ?? '';
    }
    if (Array.isArray(sourceField.value)) {
        sourceField.value = sourceField.value.map(item => item.key)[0];
    }
}

export const getFieldName = (sourceField, fieldName, getUseShippingAsBilling = false) => {
    const sectionType = sourceField.type;

    if (getUseShippingAsBilling && sectionType === 'billing') {

        fieldName = fieldName.replace('billing_', 'shipping-');
    } else if ('billing' == sectionType || 'shipping' == sectionType) {
        fieldName = fieldName.replace(sectionType + '_', sectionType + '-');
    } else if (fieldName.startsWith('billing_')) {
        fieldName = fieldName.replace('billing_', 'billing-');
    } else if (fieldName.startsWith('shipping_')) {
        fieldName = fieldName.replace('shipping_', 'shipping-');
    }

    return fieldName;
}

export const checkAndGetDefaultFieldName = (fieldName) => {
    // Map of field names to their default DOM IDs
    const defaultFieldNameMap = getDefaultFieldsName();

    if (defaultFieldNameMap.hasOwnProperty(fieldName)) {
        fieldName = defaultFieldNameMap[fieldName];
    } else {
        fieldName = 'not_default';
    }

    return fieldName;
}

// Get default fields names
export const getDefaultFieldsName = () => {
    // Map of field names to their default DOM IDs
    const defaultFieldNameMap = {
        'billing_phone': 'billing-phone',
        'billing_email': 'email',
        'billing_company': 'billing-company',
        'billing_first_name': 'billing-first_name',
        'billing_last_name': 'billing-last_name',
        'billing_country': 'billing-country',
        'billing_address_1': 'billing-address_1',
        'billing_address_2': 'billing-address_2',
        'billing_city': 'billing-city',
        'billing_state': 'billing-state',
        'billing_postcode': 'billing-postcode',
        'shipping_country': 'shipping-country',
        'shipping_first_name': 'shipping-first_name',
        'shipping_last_name': 'shipping-last_name',
        'shipping_address_1': 'shipping-address_1',
        'shipping_address_2': 'shipping-address_2',
        'shipping_city': 'shipping-city',
        'shipping_state': 'shipping-state',
        'shipping_postcode': 'shipping-postcode',
        'shipping_phone': 'shipping-phone',
        'shipping_email': 'email',
        'shipping_company': 'shipping-company',
        'order_comments': 'order_comments'
    };
    return defaultFieldNameMap;
}

export const triggerTotalsRecalc = ({ value }) => {

    // Only trigger if value is not empty
    if (value === '' || value === null || value === undefined) return;

    // Clear any previous pending recalculation
    if (recalcTimeout) {
        clearTimeout(recalcTimeout);
    }

    // Set a new timeout
    recalcTimeout = setTimeout(() => {
        // wp.data.dispatch('wc/store/cart').invalidateResolutionForStore();
        recalcTimeout = null; // reset
    }, 1000); // 1000ms = 1 second debounce
};

export const useBlockDataShare = () => {

    // Get billing fields values
    const billingData = useSelect(
        (select) => {
            const checkout = select('wc/store/checkout');
            return checkout?.__experimentalGetExtensionData?.('fme-ccfw-billing') ?? {};
        },
        []
    ) ?? {};

    // Get shipping fields values
    const shippingData = useSelect(
        (select) => {
            const checkout = select('wc/store/checkout');
            return checkout?.__experimentalGetExtensionData?.('fme-ccfw-shipping') ?? {};
        },
        []
    ) ?? {};

    // Get order notes values
    const orderNoteData = useSelect(
        (select) => {
            const checkout = select('wc/store/checkout');
            return checkout?.__experimentalGetExtensionData?.('fme-ccfw-order-note') ?? {};
        },
        []
    ) ?? {};

    return {
        billingFields: billingData?.billing_fields ?? {},
        shippingFields: shippingData?.shipping_fields ?? {},
        orderNoteFields: orderNoteData?.additional_fields ?? {},
    };
};

export const getCartCheckoutConditionValues = (cart, useShippingAsBilling = false) => {
    const billingAddress = cart?.billingAddress || {};
    const shippingAddress = cart?.shippingAddress || {};
    const cartItemCount = (cart?.items || []).reduce((total, item) => total + (item.quantity || 0), 0);

    return {
        fme_ccfw_cart_total: cart?.totals?.total_price || 0,
        fme_ccfw_cart_subtotal: cart?.totals?.total_items || 0,
        fme_ccfw_cart_item_count: cartItemCount,
        billing_first_name: billingAddress.first_name || '',
        billing_last_name: billingAddress.last_name || '',
        billing_country: billingAddress.country || '',
        billing_state: billingAddress.state || '',
        billing_city: billingAddress.city || '',
        billing_postcode: billingAddress.postcode || '',
        billing_email: billingAddress.email || '',
        billing_phone: billingAddress.phone || '',
        billing_address_1: billingAddress.address_1 || '',
        billing_address_2: billingAddress.address_2 || '',
        billing_company: billingAddress.company || '',
        shipping_first_name: shippingAddress.first_name || '',
        shipping_last_name: shippingAddress.last_name || '',
        shipping_country: shippingAddress.country || '',
        shipping_state: shippingAddress.state || '',
        shipping_city: shippingAddress.city || '',
        shipping_postcode: shippingAddress.postcode || '',
        shipping_address_1: shippingAddress.address_1 || '',
        shipping_address_2: shippingAddress.address_2 || '',
        shipping_phone: shippingAddress.phone || '',
        shipping_company: shippingAddress.company || '',
        shipping_email: shippingAddress.email || '',
        billing_useShippingAsBilling: useShippingAsBilling ? 'yes' : 'no',
        useShippingAsBilling: useShippingAsBilling ? 'yes' : 'no',
    };
};

export const setFieldValuesInLocalStorage = (fieldValues, blockName) => {

    // Store in localStorage with proper JSON serialization to preserve data types
    localStorage.setItem(`fme_${blockName}_fields`, JSON.stringify(fieldValues));

    localStorage.setItem(`fme_fields_local_val_set`, 'yes'); // Flag to indicate values are set

    if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('fme_ccfw_storage_update'));
    }

    return fieldValues;

}